import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@material-ui/core";
import { SubHeader } from "src/apps/commons/dataDisplays";
import { AsyncDropdown } from "src/apps/commons/dropdowns";
import {
  listAllRemyndCatLv1s,
  listAllRemyndCatLv2s,
  listAllRemyndCatLv3s
} from "src/apps/commons/dropdowns/api";
import { SimpleSubTable } from "src/apps/commons/form";
import { useListSubFormState } from "src/apps/commons/form/hooks";
import { IRemyndBrandBrief } from "src/apps/modules/remyndBrand/types";
import { IRemyndScrapeCategoryMappingRuleSaveReqTemp } from "../types";
import BrandDropdownModal from "./BrandDropdownModal";

const useStyles = makeStyles(_ => ({
  container: {
    "& > *": {
      padding: "12px 6px"
    }
  }
}));

type BrandModalState = {
  open: boolean;
  index?: number;
  data?: IRemyndBrandBrief;
};

interface Props {
  open: boolean;
  defaultValue?: IRemyndScrapeCategoryMappingRuleSaveReqTemp;
  handleCloseModal: () => void;
  handleSubmitModal: (
    value: IRemyndScrapeCategoryMappingRuleSaveReqTemp
  ) => void;
}

const schema = yup.object().shape({
  enabled: yup.boolean(),
  regexTh: yup.string().trim().required("กรุณาระบุ Regex (TH)"),
  regexEn: yup.string().trim().required("กรุณาระบุ Regex (EN)")
});

const MappingRuleModal: React.FC<Props> = props => {
  const classes = useStyles();
  const { open, defaultValue, handleCloseModal, handleSubmitModal } = props;

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch
  } = useForm<PartialNull<IRemyndScrapeCategoryMappingRuleSaveReqTemp>>({
    defaultValues: {
      enabled: true,
      brandMode: !defaultValue?.brands?.length ? "ALL" : "SELECT",
      ...defaultValue
    },
    resolver: yupResolver(schema)
  });

  // -------------------------------------------------------------------------

  const {
    addItem: addBrand,
    setItem: setBrand,
    delItem: delBrand,
    items: brands
  } = useListSubFormState<IRemyndBrandBrief>(defaultValue?.brands);
  const [brandModal, setBrandModal] = useState<BrandModalState>({
    open: false
  });
  const handleCloseBrandModal = () => {
    setBrandModal({ open: false, index: undefined, data: undefined });
  };
  const handleSubmitBrandModal = (req: IRemyndBrandBrief) => {
    if (brandModal.index !== undefined) {
      setBrand(req, brandModal.index);
    } else {
      addBrand(req);
    }
    handleCloseBrandModal();
  };

  // -------------------------------------------------------------------------

  const handleTransformAndSubmit = (
    form: IRemyndScrapeCategoryMappingRuleSaveReqTemp
  ) => {
    handleSubmitModal({
      ...defaultValue,
      ...form,
      brands: form.brandMode === "ALL" ? [] : brands
    });
  };

  const categoryLv1 = watch("categoryLv1");
  const categoryLv2 = watch("categoryLv2");
  const brandMode = watch("brandMode");

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        aria-labelledby="form-dialog-title"
        scroll="body"
      >
        <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
          <DialogTitle id="form-dialog-title" disableTypography>
            <Typography variant="h3">
              {!defaultValue ? "เพิ่ม " : "แก้ไข "}
              {" Mapping Rule"}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container className={classes.container}>
              <Grid item xs={12}>
                <SubHeader title="Conditions" />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="regexTh"
                  control={control}
                  defaultValue={defaultValue?.regexTh ?? ""}
                  render={({ field }) => (
                    <TextField
                      variant="outlined"
                      fullWidth
                      {...field}
                      label="Regex (TH) *"
                      placeholder="สำหรับไปเทียบกับฟีลด์ remynd_product.category_text_th"
                      error={Boolean(errors.regexTh)}
                      helperText={errors.regexTh?.message}
                      inputProps={{
                        style: { fontFamily: "monospace, 'Bai Jamjuree'" }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="regexEn"
                  control={control}
                  defaultValue={defaultValue?.regexEn ?? ""}
                  render={({ field }) => (
                    <TextField
                      variant="outlined"
                      fullWidth
                      {...field}
                      label="Regex (EN) *"
                      placeholder="สำหรับไปเทียบกับฟีลด์ remynd_product.category_text_en"
                      error={Boolean(errors.regexEn)}
                      helperText={errors.regexEn?.message}
                      inputProps={{
                        style: { fontFamily: "monospace, 'Bai Jamjuree'" }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box ml={1}>
                  <FormControlLabel
                    control={
                      <Controller
                        name="enabled"
                        control={control}
                        defaultValue={defaultValue?.enabled ?? true}
                        render={({ field: { value, onChange } }) => (
                          <Checkbox
                            checked={Boolean(value)}
                            onChange={onChange}
                            color="primary"
                          />
                        )}
                      />
                    }
                    label="Enabled"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset">
                  <Box ml={1}>
                    <Controller
                      name="brandMode"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup row {...field}>
                          <FormControlLabel
                            value="ALL"
                            control={<Radio color="primary" />}
                            label="ใช้กับทุกแบรนด์"
                          />
                          <FormControlLabel
                            value="SELECT"
                            control={<Radio color="primary" />}
                            label={
                              brandMode === "SELECT"
                                ? `เลือกแบรนด์ (${brands.length})`
                                : `เลือกแบรนด์`
                            }
                          />
                        </RadioGroup>
                      )}
                    />
                  </Box>
                </FormControl>
              </Grid>

              {brandMode === "SELECT" && (
                <Grid item xs={12}>
                  <SimpleSubTable<IRemyndBrandBrief>
                    data={brands}
                    onClickAdd={() => setBrandModal({ open: true })}
                    onClickEdit={(b, idx) =>
                      setBrandModal({
                        open: true,
                        index: idx,
                        data: b
                      })
                    }
                    onClickDelete={(_, idx) => delBrand(idx)}
                    showIndex={false}
                    columns={[
                      {
                        label: "แบรนด์",
                        renderCell: b => `${b.nameTh} (${b.nameEn})`
                      }
                    ]}
                    style={{ backgroundColor: "#EDEDED" }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <SubHeader title="Mapping Target" />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="categoryLv1"
                  control={control}
                  defaultValue={defaultValue?.categoryLv1 ?? null}
                  render={({ field: { onChange, value } }) => (
                    <AsyncDropdown
                      label="หมวดสินค้า Level 1"
                      fetchDropdown={listAllRemyndCatLv1s}
                      paginate={{
                        page: 1,
                        pageSize: 2000,
                        order: {
                          optionKey: "NAME_TH",
                          desc: false
                        }
                      }}
                      onChange={v => {
                        onChange(v);
                        setValue("categoryLv3", null);
                        setValue("categoryLv2", null);
                      }}
                      value={value}
                      error={Boolean(errors.categoryLv1)}
                      helperText={errors.categoryLv1?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="categoryLv2"
                  control={control}
                  defaultValue={defaultValue?.categoryLv2 ?? null}
                  render={({ field: { onChange, value } }) => (
                    <AsyncDropdown
                      disabled={!categoryLv1}
                      label="หมวดสินค้า Level 2"
                      fetchDropdown={listAllRemyndCatLv2s}
                      paginate={{
                        page: 1,
                        pageSize: 2000,
                        order: {
                          optionKey: "NAME_TH",
                          desc: false
                        }
                      }}
                      additionalReqParams={{
                        search: { lv1Id: categoryLv1?.value }
                      }}
                      onChange={v => {
                        onChange(v);
                        setValue("categoryLv3", null);
                      }}
                      value={value}
                      error={Boolean(errors.categoryLv2)}
                      helperText={errors.categoryLv2?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="categoryLv3"
                  control={control}
                  defaultValue={defaultValue?.categoryLv3 ?? null}
                  render={({ field: { onChange, value } }) => (
                    <AsyncDropdown
                      disabled={!categoryLv2}
                      label="หมวดสินค้า Level 3"
                      fetchDropdown={listAllRemyndCatLv3s}
                      paginate={{
                        page: 1,
                        pageSize: 2000,
                        order: {
                          optionKey: "NAME_TH",
                          desc: false
                        }
                      }}
                      additionalReqParams={{
                        search: { lv2Id: categoryLv2?.value }
                      }}
                      onChange={onChange}
                      value={value}
                      error={Boolean(errors.categoryLv3)}
                      helperText={errors.categoryLv3?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="primary">
              Cancel
            </Button>
            {!defaultValue ? (
              <Button
                key="addBtn"
                variant="contained"
                color="primary"
                type="submit"
              >
                Add
              </Button>
            ) : (
              <Button
                key="updateBtn"
                variant="contained"
                color="primary"
                type="submit"
              >
                Update
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {brandModal.open && (
        <BrandDropdownModal
          label="แบรนด์"
          open={brandModal.open}
          defaultValue={brandModal.data}
          existingValues={brands}
          handleCloseModal={handleCloseBrandModal}
          handleSubmitModal={handleSubmitBrandModal}
        />
      )}
    </>
  );
};

export default MappingRuleModal;
